import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { getLocalStorageItem } from "../utils/Validation.ts";
import { encryptData } from "../utils/helper.ts";

// Define a custom Axios request config with retry properties
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  retry?: number;
  retryDelay?: number;
}

// Create an Axios instance with a base URL
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    // Add cache control headers
    "Cache-Control": "no-cache, no-store, must-revalidate",
    "Pragma": "no-cache",
    "Expires": "0"
  },
  timeout: 60000, // 10 seconds timeout
});

// Function to check if an error is an Axios error
const isAxiosError = (error: unknown): error is AxiosError => {
  return axios.isAxiosError(error);
};

// Request Interceptor - Adds Authorization Token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getLocalStorageItem("authToken");
    const userDetails = getLocalStorageItem("detail");
    const userId = userDetails?.userId;

    if (userId) {
      // For PUT, PATCH, POST, DELETE requests
      if (
        config.method?.toLowerCase() === "put" ||
        config.method?.toLowerCase() === "patch" ||
        config.method?.toLowerCase() === "post" ||
        config.method?.toLowerCase() === "delete"
      ) {
        // Handle FormData objects
        if (config.data instanceof FormData) {
          // Only append userId if it doesn't already exist
          if (!config.data.has("userId")) {
            config.data.append("userId", userId);
          }
        }
        // Handle regular objects
        else if (config.data && typeof config.data === "object") {
          config.data = {
            ...config.data,
            userId,
          };
        }
        // If no data exists
        else if (config.data === undefined) {
          config.data = { userId };
        }
      }
      // For GET and other requests, add to URL
      else if (config.url) {
        // Check if userId already exists in the URL
        const hasUserId = config.url.includes("userId=");

        if (!hasUserId) {
          const separator = config.url.includes("?") ? "&" : "?";
          config.url = `${config.url}${separator}userId=${userId}`;
        }
      }
    }

    if (token && typeof token === "string" && token.trim() !== "") {
      const sanitizedToken = token.replace(/[^a-zA-Z0-9-_.]/g, "");
      const encryptedToken = encryptData(sanitizedToken);
      config.headers.Authorization = `Bearer ${encryptedToken}`;
    }

    // Add request interceptor to add timestamp to GET requests
    if (config.method?.toLowerCase() === 'get') {
      // Add timestamp to URL to prevent caching
      const timestamp = new Date().getTime();
      config.url = `${config.url}${config.url?.includes('?') ? '&' : '?'}_t=${timestamp}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor - Handles retries for network errors
api.interceptors.response.use(
  (response) => response,
  (err) => {
    const { config, message } = err;
    const customConfig = config as CustomAxiosRequestConfig;

    if (!customConfig || customConfig.retry === undefined) {
      return Promise.reject(err);
    }

    // Retry only for timeout or network errors
    if (!(message.includes("timeout") || message.includes("Network Error"))) {
      return Promise.reject(err);
    }

    if (customConfig.retry <= 0) {
      return Promise.reject(err);
    }

    customConfig.retry -= 1;

    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, customConfig.retryDelay || 1000);
    }).then(() => axios(customConfig));
  }
);

// Function to set retry configuration
const setRetryConfig = (
  config: AxiosRequestConfig,
 
): CustomAxiosRequestConfig => ({
  ...config,
 
});

// API Service - Common Methods (GET, POST, PUT, DELETE)
const apiService = {
get: async <T>(url: string, config: AxiosRequestConfig = {}): Promise<T> => {
  try {
   
    
    const response: AxiosResponse<T> = await api.get(
      url,
      setRetryConfig(config)
    );
    
    // Log the raw axios response

    
    // Check if response has data
    if (response.data === undefined) {
      console.error("Response data is undefined");
      throw new Error("Invalid response format: missing data");
    }
    
    return response.data;
  } catch (error) {
    console.error("GET request failed:", error);
    
    // Log more details about the error
    if (isAxiosError(error)) {
      console.error("Axios error response:", error.response);
      console.error("Axios error request:", error.request);
      console.error("Axios error config:", error.config);
      console.error("Axios error message:", error.message);
      
      if (error.response) {
        console.error("Error response status:", error.response.status);
        console.error("Error response data:", error.response.data);
      }
      
      throw error.response?.data || error;
    }
    
    throw error;
  }
},

  post: async <T>(
    url: string,
    data?: Record<string, unknown> | FormData,
    config: AxiosRequestConfig = {}
  ): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await api.post(
        url,
        data,
        setRetryConfig(config)
      );
      if (!response.data) throw new Error("Invalid response format");
      return response.data;
    } catch (error) {
      console.error("POST request failed:", error);
      if (isAxiosError(error)) throw error.response?.data;
      throw error;
    }
  },

  put: async <T>(
    url: string,
    data: Record<string, unknown>,
    config: AxiosRequestConfig = {}
  ): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await api.put(
        url,
        data,
        setRetryConfig(config)
      );
      if (!response.data) throw new Error("Invalid response format");
      return response.data;
    } catch (error) {
      console.error("PUT request failed:", error);
      if (isAxiosError(error)) throw error.response?.data;
      throw error;
    }
  },
  patch: async <T>(
    url: string,
    data: Record<string, unknown>,
    config: AxiosRequestConfig = {}
  ): Promise<T> => {
    try {
      const response: AxiosResponse<T> = await api.patch(
        url,
        data,
        setRetryConfig(config)
      );
      if (!response.data) throw new Error("Invalid response format");
      return response.data;
    } catch (error) {
      console.error("PUT request failed:", error);
      if (isAxiosError(error)) throw error.response?.data;
      throw error;
    }
  },

  delete: async <T>(
    url: string,
    data?: Record<string, unknown>,
    config: AxiosRequestConfig = {}
  ): Promise<T | null> => {
    try {
      const response: AxiosResponse<T> = await api.delete(url, {
        ...setRetryConfig(config),
        data,
      });
      return response.data || null;
    } catch (error) {
      console.error("DELETE request failed:", error);
      if (isAxiosError(error)) throw error.response?.data;
      throw error;
    }
  },
};

export const getCustomerWorkflowStatus = async (
  customerId: string
) => {
  const response = await api.get(
    `/customers/${customerId}/workflow-status`
  );

  return response.data;
};

export default apiService;
